import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DependentContainer, {
    dependentContainerFieldDefinitions,
    DependentContainerFieldsProps,
    DependentContainerProps,
} from "@store/Components/ContactUs/DependentContainer";
import InShellEditNotice from "@store/Components/InShellEditNotice/InShellEditNotice";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import debounce from "lodash/debounce";
import React, { ChangeEvent, FC, useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OptionModel {
    fields: {
        optionTitle: string;
    };
}

const enum fields {
    fieldType = "fieldType",
    prefix = "prefix",
    label = "label",
    min = "min",
    max = "max",
    hintText = "hintText",
    horizontalRadio = "horizontalRadio",
    isRequired = "isRequired",
    hiddenValue = "hiddenValue",
    options = "options",
    fieldValidationType = "fieldValidationType",
    customRegex = "customRegex",
    customIsRequiredErrorMessage = "customIsRequiredErrorMessage",
    customIsInvalidErrorMessage = "customIsInvalidErrorMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldType]: "textField" | "textArea" | "number" | "checkbox" | "radio" | "select" | "hidden";
        [fields.prefix]: string;
        [fields.label]: string;
        [fields.min]: number;
        [fields.max]: number;
        [fields.hintText]: string;
        [fields.horizontalRadio]: boolean;
        [fields.isRequired]: boolean;
        [fields.hiddenValue]: string;
        [fields.options]: OptionModel[];
        [fields.fieldValidationType]: "containsValue" | "phoneNumber" | "emailAddress" | "customRegex";
        [fields.customRegex]: string;
        [fields.customIsRequiredErrorMessage]: string;
        [fields.customIsInvalidErrorMessage]: string;
    } & DependentContainerFieldsProps;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const { prefix, label, dependentFieldKey, dependentFieldValue } = props.fields;
    const fieldKey = prefix === "" ? label : `${prefix}_${label}`;

    return {
        fieldKey,
        stateFieldValue: state.components.contactUsForm.fieldValues[fieldKey],
        /**
         * meetRequirements is used to determine if a field should be displayed / validated / in use.
         * It will either be true / false based on its dependency or be true if no dependency is provided.
         */
        meetRequirements:
            dependentFieldKey && dependentFieldValue
                ? state.components.contactUsForm.fieldValues[dependentFieldKey] === dependentFieldValue
                : true,
    };
};

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomFieldStyles {
    commonFieldWrapper?: InjectableCss;
    customFieldTextField?: TextFieldPresentationProps;
    customFieldCheckboxGroup?: CheckboxGroupPresentationProps;
    customFieldRadioTitle?: RadioGroupPresentationProps;
    customFieldTextArea?: TextAreaProps;
    customFieldSelect?: SelectPresentationProps;
}

const commonLabelProps: TypographyPresentationProps = {
    css: css`
        font-weight: ${TYPOGRAPHY.weight.semibold} !important;
        color: ${COLORS.text.dark} !important;
    `,
};

const commonErrorProps: TypographyPresentationProps = {
    css: css`
        font-weight: ${TYPOGRAPHY.weight.base} !important;
        color: ${COLORS.error} !important;
    `,
};

const commonFieldProps = {
    cssOverrides: {
        formInputWrapper: css`
            margin-bottom: 0;
        `,
    },
    labelProps: commonLabelProps,
    errorProps: commonErrorProps,
};

export const customFieldStyles: CustomFieldStyles = {
    commonFieldWrapper: {
        css: css`
            margin: 10px 0;
        `,
    },
    customFieldTextField: commonFieldProps,
    customFieldTextArea: commonFieldProps,
    customFieldSelect: commonFieldProps,
};

const styles = customFieldStyles;

const CustomField: FC<Props> = ({ fields, fieldKey, stateFieldValue, meetRequirements, setFieldValue }) => {
    const {
        fieldType,
        label,
        min,
        max,
        hintText,
        horizontalRadio,
        isRequired,
        hiddenValue,
        options,
        fieldValidationType,
        customRegex,
        customIsRequiredErrorMessage,
        customIsInvalidErrorMessage,
        dependentFieldKey,
        dependentFieldValue,
    } = fields;
    const contactFormContext = useContext(ContactFormContext);
    const [customFieldErrorMessage, setCustomFieldErrorMessage] = useState("");
    const [localFieldValue, setLocalFieldValue] = useState<string | undefined>(stateFieldValue);

    const debouncedSetFieldValue = useCallback(
        debounce((newValue: string) => {
            setFieldValue({ key: fieldKey, value: newValue });
        }, 300),
        [setFieldValue],
    );

    useEffect(() => {
        if (localFieldValue !== undefined) {
            debouncedSetFieldValue(localFieldValue);
        }
    }, [localFieldValue, debouncedSetFieldValue]);

    useEffect(() => {
        return () => {
            debouncedSetFieldValue.cancel();
        };
    }, [debouncedSetFieldValue]);

    useEffect(() => {
        contactFormContext.validators[fieldKey] = validateCustomField;
        if (typeof localFieldValue !== "undefined") {
            validateCustomField();
        }
    }, [localFieldValue]);

    /**
     * Handles ClearForm since ContactUsForm/ClearForm reducer will delete all key:value from the components/contactUsForm/fieldValues state
     */
    useEffect(() => {
        if (typeof stateFieldValue === "undefined") {
            setLocalFieldValue(stateFieldValue);
        }
    }, [stateFieldValue]);

    useEffect(() => {
        if (meetRequirements) {
            if (localFieldValue && localFieldValue !== "") {
                /**
                 * If a field was shown then hidden and then re-shown, the saved local state would be re-inserted into the application state.
                 */
                debouncedSetFieldValue(localFieldValue);
            }

            contactFormContext.validators[fieldKey] = validateCustomField;
        } else {
            /**
             * We make sure to clear field value from the application state if the field does not !meetRequirements anymore.
             * This can happen if the dependencies changed multiple time depending on the user's choices.
             * We do not want to completely remove the key:value from the application state since this would trigger the
             * ClearForm useEffect and erase our localFieldValue. A simple empty string will be ignored by the
             * ContactUsController after form submission.
             */
            debouncedSetFieldValue("");
            delete contactFormContext.validators[fieldKey];
        }
    }, [isRequired, meetRequirements]);

    useEffect(() => {
        if (fieldType === "hidden") {
            setFieldValue({ key: fieldKey, value: hiddenValue });
        }
    }, [fieldType]);

    const validateCustomField = () => {
        let errorMessage;
        const regexp = new RegExp(customRegex);

        if (isRequired) {
            switch (fieldValidationType) {
                case "phoneNumber":
                    errorMessage = !localFieldValue
                        ? customIsRequiredErrorMessage || siteMessage("ContactUsForm_PhoneNumberIsRequiredErrorMessage")
                        : !localFieldValue || validatePhone(localFieldValue)
                        ? ""
                        : customIsInvalidErrorMessage || siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
                    break;
                case "emailAddress":
                    errorMessage = !localFieldValue
                        ? customIsRequiredErrorMessage || siteMessage("ContactUsForm_EmailIsRequiredErrorMessage")
                        : !localFieldValue || validateEmail(localFieldValue)
                        ? ""
                        : customIsInvalidErrorMessage || siteMessage("ContactUsForm_EmailIsInvalidErrorMessage");
                    break;
                case "customRegex":
                    errorMessage = !localFieldValue
                        ? customIsRequiredErrorMessage || siteMessage("Field_Required", label).toString()
                        : !localFieldValue || regexp.test(localFieldValue)
                        ? ""
                        : customIsInvalidErrorMessage || siteMessage("Field_Invalid", label).toString();
                    break;

                default:
                    errorMessage = !localFieldValue
                        ? customIsRequiredErrorMessage || siteMessage("Field_Required", label).toString()
                        : "";
                    break;
            }
        }

        setCustomFieldErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const customFieldChangeHandler = (
        event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>,
    ) => {
        setLocalFieldValue(event.target.value);
    };

    const customCheckboxFieldChangeHandler: CheckboxProps["onChange"] = (_, currentValue: boolean) => {
        setLocalFieldValue(currentValue.toString());
    };

    const dependentContainerProps: DependentContainerProps = {
        dependentFieldKey,
        dependentFieldValue,
        meetRequirements,
    };

    let fieldComponent = null;

    switch (fieldType) {
        case "textField":
            fieldComponent = (
                <TextField
                    {...styles.customFieldTextField}
                    label={label}
                    required={isRequired}
                    placeholder={hintText}
                    value={localFieldValue || ""}
                    error={customFieldErrorMessage}
                    onChange={customFieldChangeHandler}
                />
            );
            break;
        case "textArea":
            fieldComponent = (
                <TextArea
                    {...styles.customFieldTextArea}
                    label={label}
                    required={isRequired}
                    placeholder={hintText}
                    value={localFieldValue || ""}
                    error={customFieldErrorMessage}
                    onChange={customFieldChangeHandler}
                />
            );
            break;
        case "number":
            fieldComponent = (
                <TextField
                    {...styles.customFieldTextField}
                    type="number"
                    label={label}
                    min={min}
                    max={max}
                    required={isRequired}
                    placeholder={hintText}
                    value={localFieldValue || ""}
                    error={customFieldErrorMessage}
                    onChange={customFieldChangeHandler}
                />
            );
            break;
        case "checkbox":
            fieldComponent = (
                <CheckboxGroup
                    required={isRequired}
                    {...styles.customFieldCheckboxGroup}
                    errorProps={commonErrorProps}
                    labelProps={commonLabelProps}
                    label={label}
                    error={customFieldErrorMessage}
                >
                    <Checkbox
                        checked={localFieldValue?.toLocaleLowerCase() === "true"}
                        onChange={customCheckboxFieldChangeHandler}
                    >
                        {hintText}
                    </Checkbox>
                </CheckboxGroup>
            );
            break;
        case "radio":
            fieldComponent = (
                <RadioGroup
                    required={isRequired}
                    {...styles.customFieldRadioTitle}
                    label={label}
                    value={localFieldValue || ""}
                    horizontal={horizontalRadio}
                    errorProps={commonErrorProps}
                    labelProps={commonLabelProps}
                    onChangeHandler={customFieldChangeHandler}
                    error={customFieldErrorMessage}
                >
                    {options.map(({ fields: { optionTitle } }) => (
                        <Radio key={optionTitle} value={optionTitle}>
                            {optionTitle}
                        </Radio>
                    ))}
                </RadioGroup>
            );
            break;
        case "select":
            fieldComponent = (
                <Select
                    required={isRequired}
                    {...styles.customFieldSelect}
                    label={label}
                    value={localFieldValue || ""}
                    onChange={customFieldChangeHandler}
                    error={customFieldErrorMessage}
                >
                    <option value="" disabled hidden>
                        {translate("Make a choice")}
                    </option>
                    {options.map(({ fields: { optionTitle } }) => (
                        <option key={optionTitle} value={optionTitle}>
                            {optionTitle}
                        </option>
                    ))}
                </Select>
            );
            break;
        default:
            break;
    }

    return (
        <DependentContainer {...dependentContainerProps}>
            <InShellEditNotice>{translate("(Edit Mode Only Notice) Field Key: {0}", fieldKey)}</InShellEditNotice>
            {fieldType === "hidden" ? (
                <InShellEditNotice>
                    {translate("Hidden Field '{0}' with value '{1}'", label, hiddenValue)}
                </InShellEditNotice>
            ) : (
                <StyledWrapper {...styles.commonFieldWrapper}>{fieldComponent}</StyledWrapper>
            )}
        </DependentContainer>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomField),
    definition: {
        group: "Contact Us",
        displayName: "Custom Field",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.fieldType,
                displayName: "Field Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Text", value: "textField" },
                    { displayName: "TextArea", value: "textArea" },
                    { displayName: "Number", value: "number" },
                    { displayName: "Checkbox", value: "checkbox" },
                    { displayName: "Radio", value: "radio" },
                    { displayName: "Select", value: "select" },
                    { displayName: "Hidden", value: "hidden" },
                ],
                defaultValue: "textField",
                fieldType: "General",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.prefix,
                displayName: "Prefix",
                tooltip: "Prefix pour regrouper les valeurs du formulaire au moment de l'envoi",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Label",
                tooltip: "Chaque champ du formulaire doit avoir une combinaison Prefix+Label unique ou Label unique",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.min,
                displayName: "Minimum",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                min: 0,
                max: 9999,
                defaultValue: 0,
                isVisible: item => item.fields.fieldType === "number",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.max,
                displayName: "Maximum",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                min: 0,
                max: 9999,
                defaultValue: 9999,
                isVisible: item => item.fields.fieldType === "number",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isVisible: item =>
                    item.fields.fieldType !== "select" &&
                    item.fields.fieldType !== "radio" &&
                    item.fields.fieldType !== "hidden",
                tab: basicTab,
            },
            {
                name: fields.horizontalRadio,
                displayName: "Display Horizontally",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: item => item.fields.fieldType === "radio",
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => item.fields.fieldType !== "hidden",
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.hiddenValue,
                displayName: "Value",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                isVisible: item => item.fields.fieldType === "hidden",
                tab: basicTab,
            },
            {
                name: fields.options,
                editorTemplate: "ListField",
                displayName: "Options",
                getDisplay: ({ fields: { optionTitle } }) => optionTitle || "Option",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "optionTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Option",
                    },
                ],
                isVisible: item => item.fields.fieldType === "radio" || item.fields.fieldType === "select",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.fieldValidationType,
                displayName: "Validation Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Contains Value", value: "containsValue" },
                    { displayName: "Phone Number", value: "phoneNumber" },
                    { displayName: "Email Address", value: "emailAddress" },
                    { displayName: "Custom Regular Expression", value: "customRegex" },
                ],
                defaultValue: "containsValue",
                fieldType: "General",
                isRequired: true,
                isVisible: item => item.fields.isRequired,
                tab: advancedTab,
            },
            {
                name: fields.customRegex,
                displayName: "Custom Regular Expression",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                isRequired: true,
                isVisible: item => item.fields.fieldValidationType === "customRegex",
                tab: advancedTab,
            },
            {
                name: fields.customIsRequiredErrorMessage,
                displayName: "Is Required Error Message",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                isVisible: item => item.fields.fieldType !== "hidden" && item.fields.isRequired,
                tab: advancedTab,
            },
            {
                name: fields.customIsInvalidErrorMessage,
                displayName: "Is Invalid Error Message",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                isVisible: item =>
                    item.fields.fieldType !== "hidden" &&
                    item.fields.isRequired &&
                    item.fields.fieldValidationType === "customRegex",
                tab: advancedTab,
            },
            ...dependentContainerFieldDefinitions,
        ],
    },
};

export default widgetModule;
